import { Inject, Injectable } from '@angular/core';
import { AuthService, TranslationService } from '@ess-front/shared';
import { getBrowserLang, HashMap, TranslocoService } from '@jsverse/transloco';
import { TranslationApiService } from '@api/translation-api.service';
import { map, Observable, take, tap } from 'rxjs';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { Title } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root',
})
export class TranslationDomainService extends TranslationService {
  constructor(
    private authService: AuthService,
    private translocoService: TranslocoService,
    private translationApiService: TranslationApiService,
    private dateAdapter: DateAdapter<Date>,
    private title: Title,
    @Inject(MAT_DATE_LOCALE) private matDateLocale: string,
  ) {
    super();
  }

  get langChange$(): Observable<string> {
    return this.translocoService.langChanges$;
  }

  translate(key: string, params?: HashMap): string {
    return this.translocoService.translate(key, params);
  }

  translateObject<T extends object>(obj: T, properties: keyof T | (keyof T)[]): T {
    if (Array.isArray(properties)) {
      let newObj = { ...obj };
      properties.forEach(property => {
        newObj = { ...newObj, [property]: this.translate(`${obj[property]}`) };
      });
      return newObj;
    }
    return { ...obj, [properties]: this.translate(`${obj[properties]}`) };
  }

  translateObjectArray<T extends object>(objArray: T[], property: keyof T | (keyof T)[]): T[] {
    return objArray.map(obj => this.translateObject(obj, property));
  }

  getUserLanguage(): string | null {
    const language = this.authService.getAuthData()?.language;
    return language ?? null;
  }

  getUserLanguageByHash$(userHash: string): Observable<string> {
    return this.translationApiService.getUserLanguageByHash$(userHash);
  }

  getTripLanguage$(tripHash: string): Observable<string> {
    return this.translationApiService.getTripLanguage$(tripHash);
  }

  getBrowserLanguage(): string {
    return getBrowserLang() ?? 'en';
  }

  getActiveLanguage(): string {
    return this.translocoService.getActiveLang();
  }

  setActiveLanguage(language: string): void {
    this.translocoService.setActiveLang(language);
    this.setDateLocale(language);
  }

  isLanguageSupported(language: string): boolean {
    return this.translocoService.isLang(language);
  }

  isTranslationLoaded$(): Observable<boolean> {
    return this.translocoService.selectTranslateObject('').pipe(map(() => true));
  }

  setTranslatedPageTitle(title: string): void {
    this.isTranslationLoaded$()
      .pipe(
        tap(() => {
          const pageTitle = this.translate(title);
          this.title.setTitle(pageTitle);
        }),
        take(1),
      )
      .subscribe();
  }

  private setDateLocale(locale: string): void {
    this.matDateLocale = locale;
    this.dateAdapter.setLocale(this.matDateLocale);
  }
}
